import { Table } from '@radix-ui/themes'
import { SortDirection } from '@tanstack/react-table'
import SortIcon from 'components/table/utility/SortIcon'
import React from 'react'
import styled from 'styled-components'

interface HeaderCellProps {
  cellText: string
  columnId: string
  headerId: string
  headerLabelId: string
  onClick?: (event: unknown) => void
  canSort?: boolean
  isSorted?: false | SortDirection
  onResizeStart?: (event: React.MouseEvent | React.TouchEvent) => void
  isResizing?: boolean
}

const StyledTableColumnHeaderCell = styled(Table.ColumnHeaderCell)`
  height: 1px; /* Hack to make the content fill the cell height */
  position: relative;
`

const StyledResizeHandle = styled.div<{ $isResizing: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 6px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    top: 20%;
    right: 2px;
    height: 60%;
    width: 2px;
    border-radius: 1px;
    background-color: ${({ theme, $isResizing }) =>
      $isResizing ? theme.forecast.secondaryText : 'transparent'};
  }

  &:hover::after {
    background-color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const StyledSortIcon = styled(SortIcon)`
  height: 1.125rem;
  width: 1.125rem;
  padding: 0.125rem;
  box-sizing: content-box;
  border-radius: 0.125rem;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.forecast.defaultGhostButton};
  position: relative;
`

const StyledHeaderCell = styled.button<{ $hasCellText: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  align-items: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-weight: 600;
  cursor: ${({ $hasCellText }) => ($hasCellText ? 'pointer' : 'default')};
  padding: 0.75rem 0.25rem;
  align-self: stretch;
  height: 100%;
  width: 100%;
  position: relative;
  &:hover {
    background-color: ${({ theme, $hasCellText }) =>
      $hasCellText ? theme.forecast.hoverGhostButton : null};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.activeText};
    outline-offset: -2px;
  }
`

export const HeaderCell: React.FC<HeaderCellProps> = ({
  cellText,
  columnId,
  headerId,
  headerLabelId,
  canSort,
  onClick,
  isSorted,
  onResizeStart,
  isResizing,
}) => {
  const hasCellText = cellText !== undefined && cellText !== ''
  const sortSuffix =
    isSorted === 'asc'
      ? ' (sorted ascending)'
      : isSorted === 'desc'
        ? ' (sorted descending)'
        : ' (unsorted)'

  return (
    <StyledTableColumnHeaderCell
      key={columnId}
      id={headerId}
      scope='col'
      role='columnheader'
      aria-sort={
        isSorted ? (isSorted === 'asc' ? 'ascending' : 'descending') : 'none'
      }
      tabIndex={-1}>
      <StyledHeaderCell
        $hasCellText={hasCellText}
        onClick={onClick}
        role='button'
        tabIndex={0}
        aria-hidden={!hasCellText}
        aria-label={`${hasCellText ? cellText : 'Placeholder'}${sortSuffix}`}>
        <span id={headerLabelId}>{cellText}</span>
        {/* eslint-disable-next-line no-classname/no-classname-outside-designsystem */}
        {!hasCellText && <span className='sr-only'>Placeholder</span>}

        {canSort && isSorted && hasCellText ? (
          isSorted === 'asc' ? (
            <StyledSortIcon sortDirection='asc' />
          ) : isSorted === 'desc' ? (
            <StyledSortIcon sortDirection='desc' />
          ) : undefined
        ) : undefined}
      </StyledHeaderCell>
      {onResizeStart && (
        <StyledResizeHandle
          $isResizing={Boolean(isResizing)}
          role='separator'
          aria-orientation='vertical'
          aria-label='Resize column'
          onMouseDown={onResizeStart}
          onTouchStart={onResizeStart}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </StyledTableColumnHeaderCell>
  )
}
